/**
 * 画面閲覧制限（フェーズ1: フロント制御のみ）の判定ユーティリティ
 *
 * 制限設定は T_CEAS_PROJECT.screenViewRestriction に JSON 文字列で保存される。
 *   例: '{"sales":["6","7","10","11"]}'（null／属性なし＝制限なし）
 * サイドバー・renderComponent ガード・申請書・制限モーダルで共有する純関数のみを置く。
 */
import { ComfirmDivisions, ViewRestrictableScreenIds, WorkflowId } from "../contexts/constants";

/** 制限設定（キーは担当区分。フェーズ1は営業のみ） */
export type ViewRestriction = { sales?: string[] };

/**
 * JSON文字列を解釈する。null・空・不正は null（制限なし）
 * 不正値は BE の入力検証で弾かれる前提だが、念のため警告を出す
 */
export const parseViewRestriction = (json?: string | null): ViewRestriction | null => {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      console.warn("parseViewRestriction: invalid format", json);
      return null;
    }
    const sales = Array.isArray(parsed.sales) ? parsed.sales.filter((id: unknown) => typeof id === "string") : [];
    return sales.length > 0 ? { sales } : null;
  } catch (e) {
    console.warn("parseViewRestriction: parse error", e);
    return null;
  }
};

/** 選択した画面IDの配列 → 保存用JSON文字列（空なら null） */
export const serializeViewRestriction = (salesScreenIds: string[]): string | null => {
  // 表示順（ViewRestrictableScreenIds の順）にそろえ、重複と許可外の値を除く
  const sales = ViewRestrictableScreenIds.filter((id) => salesScreenIds.includes(id));
  return sales.length > 0 ? JSON.stringify({ sales }) : null;
};

/** 案件に営業向けの制限が1画面以上あるか（ログインユーザー非依存）。申請時の営業確認免除・免除表示で使う */
export const hasSalesRestriction = (restriction: ViewRestriction | null): boolean =>
  (restriction?.sales?.length ?? 0) > 0;

/** 制限対象ユーザーの判定に使う情報 */
export type RestrictedUserArgs = {
  /** ログインユーザーの社員ID */
  loginEmployeeId: string;
  /** ログインユーザーの所属組織ID（複数所属） */
  loginAffiliationOrgIds: string[];
  /** 起案者の社員ID */
  registrantEmployeeId?: string | null;
  /** 起案部署ID */
  registrantDivisionId?: string | null;
  /** 確認者一覧（getProjectById.confirmEmployees） */
  confirmEmployees: { employeeId: string; divisionId: string }[];
  /** 承認者の社員ID一覧（getApproverList） */
  approverEmployeeIds: string[];
};

/**
 * 承認者以外の条件で「制限を受ける営業確認者」の候補か
 * = 営業確認者の行がある AND 起案者本人でない AND 起案部署に所属していない AND 他区分の確認者の行がない
 */
const isSalesOnlyConfirmor = (args: Omit<RestrictedUserArgs, "approverEmployeeIds">): boolean => {
  const { loginEmployeeId, loginAffiliationOrgIds, registrantEmployeeId, registrantDivisionId, confirmEmployees } = args;
  if (!loginEmployeeId) return false;
  const myRows = confirmEmployees.filter((c) => c.employeeId === loginEmployeeId);
  if (!myRows.some((c) => c.divisionId === ComfirmDivisions.salesDivision)) return false;
  if (registrantEmployeeId && registrantEmployeeId === loginEmployeeId) return false;
  if (registrantDivisionId && loginAffiliationOrgIds.includes(registrantDivisionId)) return false;
  if (myRows.some((c) => c.divisionId !== ComfirmDivisions.salesDivision)) return false;
  return true;
};

/** ログインユーザーが「制限を受ける営業確認者」か（営業より強い立場を1つでも持てば制限しない） */
export const isRestrictedSalesUser = (args: RestrictedUserArgs): boolean =>
  isSalesOnlyConfirmor(args) && !args.approverEmployeeIds.includes(args.loginEmployeeId);

/** 承認者一覧の取得が必要か（制限がない、または承認者判定以外の条件で制限対象外と決まるなら不要） */
export const needsApproverCheck = (
  args: Omit<RestrictedUserArgs, "approverEmployeeIds"> & { restriction: ViewRestriction | null }
): boolean => hasSalesRestriction(args.restriction) && isSalesOnlyConfirmor(args);

/** 親画面が持つ判定状態。undefined = 未取得（制限可能画面を「制限あり」として扱う） */
export type ViewRestrictionState = {
  restriction: ViewRestriction | null;
  isRestrictedUser: boolean;
} | undefined;

/** 画面を隠すか */
export const isScreenHidden = (screenId: string, state: ViewRestrictionState): boolean => {
  const isTarget = ViewRestrictableScreenIds.includes(screenId) || screenId === WorkflowId.ReportAndDraft;
  if (!isTarget) return false;
  if (state === undefined) return true;
  if (!state.isRestrictedUser) return false;
  if (screenId === WorkflowId.ReportAndDraft) return hasSalesRestriction(state.restriction);
  return state.restriction?.sales?.includes(screenId) ?? false;
};

/**
 * 案件コピーを禁止するか。コピーすると実行者が起案者になり制限が外れるため、制限を受けるユーザーにはコピーさせない
 * 未取得なら禁止（制限側に倒す）
 */
export const isProjectCopyRestricted = (state: ViewRestrictionState): boolean => {
  if (state === undefined) return true;
  return state.isRestrictedUser && hasSalesRestriction(state.restriction);
};

/** ScreenUsageMap で Simulation（10/11 のうち目的に応じて表示される方）を表す値 */
export const SimulationUsageId = "simulation";

/**
 * 依存画面一覧: 制限する画面ID → その画面のデータを使用している画面
 * noteKey は画面名の後ろに付ける注記の i18n キー（features.InvestmentScreening.BasicInfo.label 配下）
 * 過去PJ由来の依存（8←7、11←10）は自案件の制限では防げないため載せない
 */
export const ScreenUsageMap: Record<string, { screenId: string; noteKey?: string }[]> = {
  [WorkflowId.ProductionProcess]: [
    { screenId: WorkflowId.Equipment },
    { screenId: WorkflowId.ProfitAndLoss, noteKey: "viewRestrictionAutoCalc" },
    { screenId: WorkflowId.ProjectProfitablityStatement },
    { screenId: SimulationUsageId },
    { screenId: WorkflowId.ReviewBusiness },
  ],
  [WorkflowId.Equipment]: [
    { screenId: WorkflowId.ProjectProfitablityStatement },
    { screenId: SimulationUsageId },
    { screenId: WorkflowId.ReviewBusiness },
  ],
  [WorkflowId.ProfitAndLoss]: [
    { screenId: WorkflowId.ProjectProfitablityStatement },
    { screenId: SimulationUsageId },
    { screenId: WorkflowId.ReviewBusiness },
  ],
  [WorkflowId.ProjectProfitablityStatement]: [
    { screenId: SimulationUsageId },
    { screenId: WorkflowId.ReviewBusiness },
  ],
};
